import { z } from 'zod';
import { isoDateTimeSchema, localeSchema, uuidSchema } from './common.js';
import { measurementSystemSchema, userRoleSchema, userStatusSchema } from './enums.js';
import { BACKGROUND_DIM_DEFAULT, backgroundDimSchema, backgroundStyleSchema, colorModeSchema, themeSchema, } from './themes.js';
export const passwordSchema = z
    .string()
    .min(10, 'Password must be at least 10 characters')
    .max(128)
    .refine((v) => /[a-zA-Z]/.test(v) && /[0-9]/.test(v), 'Password must contain letters and numbers');
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const registerRequestSchema = z.object({
    email: emailSchema,
    displayName: z.string().trim().min(1).max(80),
    password: passwordSchema,
    locale: localeSchema.default('de'),
    /** Proposed by the client from the browser's region; language and units are two settings. */
    measurementSystem: measurementSystemSchema.optional(),
    acceptTerms: z.literal(true),
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(128),
    deviceName: z.string().max(120).optional(),
});
export const currentUserSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    emailVerified: z.boolean(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
    locale: localeSchema,
    measurementSystem: measurementSystemSchema,
    defaultServings: z.number().int().positive(),
    role: userRoleSchema,
    status: userStatusSchema,
    aiEnabled: z.boolean(),
    aiConsentAt: isoDateTimeSchema.nullable(),
    /** Appearance travels with the session so the look is right before the profile is loaded. */
    theme: themeSchema.default('classic'),
    colorMode: colorModeSchema.default('SYSTEM'),
    backgroundStyle: backgroundStyleSchema.default('PATTERN'),
    backgroundDim: backgroundDimSchema.default(BACKGROUND_DIM_DEFAULT),
    backgroundUrl: z.string().nullable().default(null),
    createdAt: isoDateTimeSchema,
});
/**
 * Native shells (Capacitor) cannot use the SameSite=Strict refresh cookie: their origin is
 * capacitor://localhost. They send `X-Client-Platform: native`, receive the refresh token in
 * the body, keep it in Keychain/Keystore and present it in `X-Refresh-Token`. In this mode
 * the server ignores and never sets cookies (docs/finished/tasting-notes-spec.md 11).
 */
export const NATIVE_CLIENT_HEADER = 'x-client-platform';
export const NATIVE_CLIENT_VALUE = 'native';
export const REFRESH_TOKEN_HEADER = 'x-refresh-token';
/**
 * The access token is returned in the body and kept in memory by the client.
 * For browsers the refresh token is set as an HttpOnly cookie and never exposed to JavaScript.
 */
export const authTokensSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: isoDateTimeSchema,
    user: currentUserSchema,
    /** Only present for native clients (see NATIVE_CLIENT_HEADER). */
    refreshToken: z.string().optional(),
    refreshTokenExpiresAt: isoDateTimeSchema.optional(),
});
export const registerResponseSchema = z.object({
    user: currentUserSchema,
    verificationRequired: z.boolean(),
    tokens: authTokensSchema.nullable(),
});
export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(16).max(512),
    password: passwordSchema,
});
export const verifyEmailRequestSchema = z.object({ token: z.string().min(16).max(512) });
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1).max(128),
    newPassword: passwordSchema,
});
export const sessionSchema = z.object({
    id: uuidSchema,
    deviceInfo: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    lastUsedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema,
    current: z.boolean(),
});
/** Who signs users in: this API, or the central identity service (FAPS Account). */
export const authModeSchema = z.enum(['local', 'oidc']);
/**
 * Native shell, AUTH_MODE=oidc: the sign-in ran in the system browser and returned into the app
 * with a one-time code; the app trades it for its session here.
 */
export const oidcNativeExchangeRequestSchema = z.object({ code: z.string().min(16).max(512) });
export const accessTokenClaimsSchema = z.object({
    sub: uuidSchema,
    role: userRoleSchema,
    sid: uuidSchema,
    iat: z.number(),
    exp: z.number(),
});
//# sourceMappingURL=auth.js.map